// Helper type to make specific keys non-null
type MakePropertiesNonNull<Obj, Keys extends keyof Obj> = Omit<Obj, Keys> & {
  [P in Keys]: NonNullable<Obj[P]>;
};

/**
 * Check that an object is not null and that every property is not null.
 */
export default function isNonNull<Obj extends object | null | undefined>(
  object: Obj
): object is NonNull<NonNullable<Obj>>;

/**
 * Check that an object is not null and that specific properties are not null.
 * @param object - The object to check
 * @param properties - Array of property keys to check for non-null values
 * @example
 * const user = { name: 'John', age: 30, address: null };
 * if (isNonNull(user, ['name', 'age'])) {
 *   // TypeScript knows name and age are non-null, but address could still be null
 *   console.log(user.name, user.age);
 * }
 */
export default function isNonNull<
  Obj extends object | null | undefined,
  Keys extends keyof NonNullable<Obj>,
>(
  object: Obj,
  properties: readonly Keys[]
): object is Prettify<NonNullable<Obj> & MakePropertiesNonNull<NonNullable<Obj>, Keys>>;

export default function isNonNull<
  Obj extends object | null | undefined,
  Keys extends keyof NonNullable<Obj>,
>(object: Obj, properties?: readonly Keys[]): boolean {
  if (object === null || object === undefined) return false;

  if (properties === undefined) {
    return Object.values(object).every((value) => value !== null);
  }

  return properties.every((key) => (object as NonNullable<Obj>)[key] !== null);
}
